import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2, AlertTriangle } from 'lucide-react';
import { serversApi, DiscoveryJobStatus } from '../../lib/api/servers';
import { Button } from '../ui/Button';

interface DiscoveryProgressProps {
  serverId: string;
  jobId: string;
  onComplete: () => void;
}

export function DiscoveryProgress({ serverId, jobId, onComplete }: DiscoveryProgressProps) {
  const [status, setStatus] = useState<DiscoveryJobStatus | null>(null);
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    const pollStatus = async () => {
      try {
        const jobStatus = await serversApi.getDiscoveryStatus(serverId, jobId);
        setStatus(jobStatus);

        // Stop polling if job is complete
        if (['success', 'partial', 'failed'].includes(jobStatus.status)) {
          setPolling(false);
        }
      } catch (error) {
        console.error('Failed to fetch discovery status:', error);
        setPolling(false);
      }
    };

    // Initial fetch
    pollStatus();

    // Poll every 2 seconds while job is running
    const interval = setInterval(() => {
      if (polling) {
        pollStatus();
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [serverId, jobId, polling]);

  if (!status) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const getStatusIcon = () => {
    switch (status.status) {
      case 'success':
        return <CheckCircle className="w-12 h-12 text-green-500" />;
      case 'partial':
        return <AlertTriangle className="w-12 h-12 text-yellow-500" />;
      case 'failed':
        return <XCircle className="w-12 h-12 text-red-500" />;
      default:
        return <Loader2 className="w-12 h-12 animate-spin text-blue-500" />;
    }
  };

  const getStatusText = () => {
    switch (status.status) {
      case 'success':
        return 'Discovery completed successfully!';
      case 'partial':
        return 'Discovery completed with some errors';
      case 'failed':
        return 'Discovery failed';
      case 'running':
        return status.phase || 'Running discovery...';
      default:
        return 'Starting discovery...';
    }
  };

  const isComplete = ['success', 'partial', 'failed'].includes(status.status);

  return (
    <div className="space-y-6">
      {/* Status Icon and Text */}
      <div className="flex flex-col items-center justify-center space-y-4">
        {getStatusIcon()}
        <h3 className="text-xl font-semibold text-center">{getStatusText()}</h3>
      </div>

      {/* Progress Bar */}
      {!isComplete && (
        <div className="space-y-2">
          <div className="flex justify-between text-sm text-gray-600">
            <span>{status.phase || 'Starting...'}</span>
            <span>{status.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-blue-500 h-3 rounded-full transition-all duration-300"
              style={{ width: `${status.progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Results */}
      {isComplete && (
        <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{status.docker_apps_count}</div>
            <div className="text-sm text-gray-600">Docker Apps</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{status.static_apps_count}</div>
            <div className="text-sm text-gray-600">Static Apps</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{status.domains_count}</div>
            <div className="text-sm text-gray-600">Domains</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{status.environments_count}</div>
            <div className="text-sm text-gray-600">Environments</div>
          </div>
        </div>
      )}

      {/* Errors */}
      {status.errors && status.errors.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-red-700">Errors:</h4>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {status.errors.map((error, index) => (
              <div key={index} className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {error}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3">
        {isComplete ? (
          <Button onClick={onComplete}>
            Close
          </Button>
        ) : (
          <Button onClick={onComplete} variant="outline">
            Close (background)
          </Button>
        )}
      </div>

      {/* Timestamps */}
      {(status.started_at || status.completed_at) && (
        <div className="text-xs text-gray-500 space-y-1 border-t pt-4">
          {status.started_at && (
            <div>Started: {new Date(status.started_at).toLocaleString()}</div>
          )}
          {status.completed_at && (
            <div>Completed: {new Date(status.completed_at).toLocaleString()}</div>
          )}
        </div>
      )}
    </div>
  );
}
